import { Link } from "react-router-dom";
import { PERSONALIZED_PRODUCTS } from "@/lib/products";
import { Sparkles, ArrowUpRight } from "lucide-react";

// Rakhi / Bhai-Gifting spotlight — surfaces the 18ct gold stretch nameplate bracelet
// as the entry-level fine-gold gift for Rakhi season.
export default function RakhiGiftingSection() {
  const product = PERSONALIZED_PRODUCTS.find(
    (p) => p.id === "gold-nameplate-stretch-bracelet"
  );
  if (!product) return null;

  const hero = product.gallery?.[0] || product.image;
  const collage = (product.gallery || []).slice(1, 5);
  const samplesHref = `/samples/${product.productType}/${product.designType}/${product.weights?.[0] || "1g"}`;

  return (
    <section
      id="rakhi"
      data-testid="rakhi-section"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      {/* Soft warm gold wash behind */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-[radial-gradient(closest-side,rgba(212,175,55,0.28),transparent_70%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left · story */}
        <div>
          <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 border border-[#d4af37]/40 mb-6">
            <Sparkles size={14} className="text-[#d4af37]" />
            <span className="text-[11px] tracking-widest-2 uppercase text-[#d4af37]">
              Rakhi · Bhai Gifting Edit
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] text-white">
            A little <span className="gold-gradient-text italic">gold</span> on his wrist,
            <br className="hidden md:block" />
            forever.
          </h2>
          <p className="mt-6 max-w-xl text-white/70 md:text-lg leading-relaxed">
            Meet the daily-wear <span className="text-white">18 ct gold nameplate stretch bracelet</span>
            {" — "}the natural entry-level fine-gold Rakhi gift. Personalise it with his initials, name or a
            sacred symbol; slip it onto a mesh or Rolex-style belt in his favourite colour.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            <MiniStat k="1 – 10g" v="18ct gold range" />
            <MiniStat k="19" v="Real designs" />
            <MiniStat k="2–3" v="Days in Hyderabad" />
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to={samplesHref}
              data-testid="rakhi-view-designs-cta"
              className="pulse-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-[#d4af37] text-black font-medium tracking-wide hover:brightness-110 transition"
            >
              View All 19 Designs
              <ArrowUpRight size={18} />
            </Link>
            <a
              href="#personalize"
              data-testid="rakhi-personalize-cta"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 glass glass-hover text-white/90 border border-white/15 transition"
            >
              Personalise Yours
            </a>
          </div>
        </div>

        {/* Right · collage */}
        <div className="relative">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[#d4af37]/25 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.35)]">
            <img
              src={hero}
              alt="Gold Nameplate Stretch Bracelet — Rakhi Edit"
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
              <div>
                <div className="text-[10px] tracking-widest-2 uppercase text-[#d4af37]">
                  Featured
                </div>
                <div className="font-display text-2xl text-white leading-tight">
                  {product.name}
                </div>
              </div>
              <div className="text-white/70 text-xs uppercase tracking-widest-2 whitespace-nowrap">
                From 1g · 18ct
              </div>
            </div>
          </div>

          {/* 2×2 mini-collage of alternate designs */}
          <div className="mt-4 grid grid-cols-4 gap-2 md:gap-3">
            {collage.map((src, i) => (
              <Link
                key={i}
                to={samplesHref}
                data-testid={`rakhi-mini-${i}`}
                className="group relative aspect-square rounded-xl overflow-hidden border border-white/10 hover:border-[#d4af37]/60 transition"
              >
                <img
                  src={src}
                  alt={`Design ${i + 2}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const MiniStat = ({ k, v }) => (
  <div>
    <div className="font-display text-2xl md:text-3xl text-white">{k}</div>
    <div className="text-[10px] uppercase tracking-widest-2 text-white/50 mt-1">{v}</div>
  </div>
);
